import { lazy, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";

import { useUserData } from "src/context/userContext";
import { useStepLogData } from "src/context/StepLogContext";
import { useKeikkaData } from "src/context/KeikkaDataContext";
import { useKeikkaBetoniData } from "src/context/KeikkaBetoniContext";
import { useNavData } from "src/context/navContext";
import { isKeikkaHandler } from "src/data/ifs/isIfs";
import { keikkaStars_fetch } from "src/data/starsClass";
import { parseJSONDate, pvmString } from "src/components/js/statics1";
import keikkaClass from "src/data/keikka/keikkaClass";
import ProcessClass from "src/data/keikka/processClass";

import ProcessRow from "./components/ProcessRow";
import ProcentBar from "./components/ProcentBar";
import ProcessHeadingCell from "./components/ProcessHeadingCell";
import ProcessSubHeadingRow from "./components/ProcessSubHeadingRow";
import TabSubOtsikko from "../components/TabSubOtsikko";
import ProcessRowOsoite from "./components/processRows/ProcessRowOsoite";
import ProcessRowAika from "./components/processRows/ProcessRowAika";
import ProcessRowAsiakas from "./components/processRows/ProcessRowAsiakas";
import ProcessRowTilaus from "./components/processRows/ProcessRowTilaus";
import ProcessRowPumppu from "./components/processRows/ProcessRowPumppu";
import ProcessRowBetoni from "./components/processRows/ProcessRowBetoni";
import ProcessRowVastaanotto from "./components/processRows/ProcessRowVastaanotto";
import ProcessRowTilausvahvistus from "./components/processRows/ProcessRowTilausvahvistus";
import ProcessRowToimitusvalmis from "./components/processRows/ProcessRowToimitusvalmis";
import LazyLoader from "src/components/layout/Progress/LazyLoader";
import ExpandCollapseIcon from "src/components/icons/purposed/ExpandCollapseIcon";

const _confirmRightButtonModal = import(
  "../StepTilaus/Modals/confirmKeikka/ConfirmRightButtonModal"
);
const ConfirmRightButtonModal = lazy(() => _confirmRightButtonModal);

const _handleKeikkaModal = import("../StepTilaus/Modals/handleKeikka/HandleKeikkaModal");
const HandleKeikkaModal = lazy(() => _handleKeikkaModal);

const Stars = lazy(() => import("./components/Stars/Stars"));
const StarsSelectors = lazy(() => import("./components/Stars/StarsSelectors"));

export default function StepProcess() {
  const { user, token, socket } = useUserData();
  const { keikkaData, doSetKeikkaData: setKeikkaData } = useKeikkaData();
  const { stepLog } = useStepLogData();
  const { doSetStepId: setStepId } = useNavData();
  const { doSetKeikkaBetoniId: setKeikkaBetoniId } = useKeikkaBetoniData();

  const personId = user.personId;

  // eslint-disable-next-line no-unused-vars
  const [thisProcessJSON, setThisProcessJSON] = useState();
  const [thisProcess, setThisProcess] = useState(new ProcessClass(new keikkaClass()));
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [starsLoading, setStarsLoading] = useState(true);
  const [handleKeikkaModalIsOpen, setHandleKeikkaModalIsOpen] = useState(false);
  const [confirmRightButtonModalIsOpen, setConfirmRightButtonModalIsOpen] = useState(false);
  const [stars, setStars] = useState(0);

  const stepLogListener = useCallback(async () => {
    console.log("Heard stepLog:update");
    setIsLoading(true);
    doSetThisProcess(await initProcessClass(token));
    setIsLoading(false);
  });

  useEffect(() => {
    let isMounted = true;
    socket.on("stepLog:update", stepLogListener);
    const asyncFunc1 = async () => {
      // initialize processClass
      setIsLoading(true);
      asyncFunc2(token);
      const _processClass = await initProcessClass(token);
      if (isMounted) {
        doSetThisProcess(_processClass);
        setIsLoading(false);
      }
    };
    const asyncFunc2 = async (token) => {
      // initialize stars
      await initStars(token);
      if (isMounted) setStarsLoading(false);
    };
    asyncFunc1();
    if (keikkaData.tila.keikkaTilaId === 9) {
      // jos keikka on valmis, avataan arvosana välilehti
      isMounted && setExpanded("arvosana");
    } else if (keikkaData.tila.keikkaTilaId < 3) {
      // jos keikka on kesken/luonnos, avataan tilaus välilehti
      isMounted && setExpanded("luonti");
    }
    return () => {
      socket && socket.off("stepLog:update", stepLogListener);
    };
  }, []);

  const initProcessClass = async (token) => {
    const _processClass = new ProcessClass(keikkaData, personId);
    await _processClass.initProcess(token, socket);
    return _processClass;
  };

  const initStars = async (token) => {
    const _stars = await keikkaStars_fetch(keikkaData.keikkaId, token);
    setStars(_stars);
    keikkaData.stars.stars = _stars;
    setKeikkaData(keikkaData);
  };

  const handleChange = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      if (panel === "luonti" && !keikkaData.auth.originalEdit) return;
      if (panel === "käsittely" && !keikkaData.auth.isKeikkaAdmin) return;
      if (panel === "toimitus" && !keikkaData.auth.isKeikkaAdmin) return;
      if (panel === "arvosana" && !keikkaData.auth.originalEdit) return;
    }
    setExpanded(isExpanded ? panel : false);
  };
  console.log(keikkaData.tyomaa.tyomaaContactPersonId);
  const reloadProcess = async () => {
    setIsLoading(true);
    const pc = await initProcessClass(token);
    setThisProcess(pc);
    setIsLoading(false);
  };

  const doSetThisProcess = (_p) => {
    setThisProcess(_p);
    setThisProcessJSON(_p.hash);
  };

  const navTo = (step) => setStepId(step);
  const handleStarsChange = async (stars) => {
    // get average stars
    let tot = 0;
    let cnt = 0;
    if (stars.betoni.stars > 0) {
      cnt++;
      tot += stars.betoni.stars;
    }
    if (stars.kuljetukset.stars > 0) {
      cnt++;
      tot += stars.kuljetukset.stars;
    }
    if (stars.pumppaus.stars > 0) {
      cnt++;
      tot += stars.pumppaus.stars;
    }
    if (stars.sovellus.stars > 0) {
      cnt++;
      tot += stars.sovellus.stars;
    }
    tot = tot / cnt;
    keikkaData.stars.stars = tot;
    setKeikkaData(keikkaData);
    console.log(stars);
    setStars(tot);
  };

  if (!thisProcess) return null;

  return (
    <>
      <LazyLoader>
        <HandleKeikkaModal
          open={handleKeikkaModalIsOpen}
          closeModal={() => setHandleKeikkaModalIsOpen(false)}
        />
        <ConfirmRightButtonModal
          open={confirmRightButtonModalIsOpen}
          closeModal={() => setConfirmRightButtonModalIsOpen(false)}
        />
      </LazyLoader>
      <TabSubOtsikko
        txt="Tämä sivu näkyy vain mikäli käyttäjä olisi saanut muokata tilausta luontivaiheessa"
        show={false}
      />
      <div style={{ padding: "8px" }}>
        {Arvosana()}
        {Luonti()}
        {Käsittely()}
        {Toimitus()}
        {Seuranta()}
      </div>
    </>
  );

  function Seuranta() {
    return (
      keikkaData.tila.keikkaTilaId < 5 && (
        <Accordion expanded={false} disabled={true}>
          <AccordionSummary
            expandIcon={<ExpandCollapseIcon isLoading={isLoading} />}
            aria-controls="toimitusbh-content"
            id="toimitusbh-header">
            <ProcessHeadingCell txt={"Seuranta"} />
            <ProcentBar key={isLoading} isLoading={isLoading} procent={0} />
          </AccordionSummary>
        </Accordion>
      )
    );
  }

  // function OmaNäkyvyys() {
  //   return (
  //     <Accordion expanded={expanded === "näkyvyys"} onChange={handleChange("näkyvyys")}>
  //       <AccordionSummary
  //         expandIcon={<ExpandCollapseIcon />}
  //         aria-controls="toimitusbh-content"
  //         id="toimitusbh-header">
  //         <ProcessHeadingCell txt={"Näkyvyys"} />
  //       </AccordionSummary>
  //       <AccordionDetails>
  //         {keikkaData.auth.isKeikkaAdmin && renderAuthText("Olet tilauksen keikka admin")}
  //         {keikkaData.auth.isBetoniAdmin && renderAuthText("Olet tilauksen betoni admin")}
  //         {keikkaData.auth.isPumppuAdmin && renderAuthText("Olet tilauksen pumppu admin")}
  //         {keikkaData.keikkaContactPerson.personId === personId &&
  //           renderAuthText("Olet tilauksen yhteyshenkilö")}
  //         {keikkaData.auth.isPumppari && renderAuthText("Olet pumppari")}
  //         {keikkaData.asiakas.auth.edit && renderAuthText("Olet asiakkaan henkilö")}
  //         {keikkaData.asiakas.auth.isAsiakasAdmin && renderAuthText("Olet asiakkaan hallinnoija")}
  //         {keikkaData.asiakas.auth.isAsiakasEditor && renderAuthText("Olet asiakkaan muokkaaja")}
  //         {keikkaData.asiakas.auth.isTarjousAdmin && renderAuthText("Olet asiakkaan tarjous admin")}
  //         {keikkaData.asiakas.asiakasContactPersonId === personId &&
  //           renderAuthText("Olet asiakkaan yhteyshenkilö")}
  //         {keikkaData.tyomaa.auth.edit && renderAuthText("Olet työmaan henkilö")}
  //         {keikkaData.tyomaa.tyomaaContactPersonId === personId &&
  //           renderAuthText("Olet työmaan yhteyshenkilö")}
  //       </AccordionDetails>
  //     </Accordion>
  //   );
  // }

  function Toimitus() {
    return (
      <Accordion
        expanded={expanded === "toimitus"}
        onChange={handleChange("toimitus")}
        disabled={!isKeikkaHandler(keikkaData, user, stepLog)}>
        <AccordionSummary
          expandIcon={<ExpandCollapseIcon isLoading={isLoading} />}
          aria-controls="toimitusbh-content"
          id="toimitusbh-header">
          <ProcessHeadingCell txt="Toimitus" />
          <ProcentBar
            key={isLoading}
            isLoading={isLoading}
            procent={thisProcess.tilausToimitetaanPercent}
          />
        </AccordionSummary>
        <AccordionDetails>
          <TabSubOtsikko
            txt="Tämä aukeaa vain mikäli käyttäjä on isKeikkaAdmin"
            show={false}
            noDivider
          />
          <ProcessSubHeadingRow txt="Tilaus on hyväksytty ja on valmis toimitettavaksi" />
          <ProcessRowToimitusvalmis thisProcess={thisProcess} setThisProcess={doSetThisProcess} />

          <ProcessRow txt="Ilmoitus kuljettajalle (todo)" done={false} />
          <ProcessRow
            txt="Tilaus toimitettu"
            tip="Merkitse tilaus toimitetuksi"
            done={thisProcess.tilausToimitettu.isConfirmed}
            pvm={thisProcess.tilausToimitettu.confirmDate}
            enabled={!thisProcess.tilausToimitettu.isConfirmed}
            onClick={() => { }}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  function Käsittely() {
    return (
      <Accordion
        expanded={expanded === "käsittely"}
        onChange={handleChange("käsittely")}
        disabled={!isKeikkaHandler(keikkaData, user, stepLog)}>
        <AccordionSummary
          expandIcon={<ExpandCollapseIcon isLoading={isLoading} />}
          aria-controls="käsittelybh-content"
          id="käsittelybh-header">
          <ProcessHeadingCell txt="Käsittely" />
          <ProcentBar
            key={isLoading}
            isLoading={isLoading}
            procent={thisProcess.betoniToimijaKäsitteleeTilaustaPercent}
          />
        </AccordionSummary>
        <AccordionDetails>
          <TabSubOtsikko
            txt="Tämä aukeaa vain mikäli käyttäjä on isKeikkaHandler"
            show={false}
            noDivider
          />
          <ProcessSubHeadingRow txt="Tilaus on luotu ja asiakas on lähettänyt tilauksen" />
          <ProcessRowVastaanotto
            thisProcess={thisProcess}
            setThisProcess={doSetThisProcess}
            setKeikkaData={setKeikkaData}
          />
          <ProcessRowAika thisProcess={thisProcess} setThisProcess={doSetThisProcess} />
          <ProcessRowPumppu
            thisProcess={thisProcess}
            setThisProcess={doSetThisProcess}
            show={keikkaData.modules.hasPumppu}
          />
          <ProcessRowBetoni
            thisProcess={thisProcess}
            setThisProcess={doSetThisProcess}
            show={keikkaData.modules.hasBetoni}
          />
          <ProcessRowAsiakas thisProcess={thisProcess} setThisProcess={doSetThisProcess} />
          <ProcessRowOsoite thisProcess={thisProcess} setThisProcess={doSetThisProcess} />
          <ProcessRowTilaus thisProcess={thisProcess} setThisProcess={doSetThisProcess} />
          <ProcessRowTilausvahvistus
            thisProcess={thisProcess}
            reloadProcess={reloadProcess}
            setThisProcess={doSetThisProcess}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  function Luonti() {
    return (
      <Accordion expanded={expanded === "luonti"} onChange={handleChange("luonti")}>
        <AccordionSummary
          expandIcon={<ExpandCollapseIcon isLoading={isLoading} />}
          aria-controls="luontibh-content"
          id="luontibh-header">
          <ProcessHeadingCell txt="Tilaus" />
          <ProcentBar
            key={isLoading}
            isLoading={isLoading}
            procent={thisProcess.asiakasLuoTilaustaPercent}
          />
        </AccordionSummary>
        <AccordionDetails>
          <TabSubOtsikko
            txt="Tämä aukeaa vain mikäli käyttäjä olisi saanut muokata tilausta luontivaiheessa"
            show={keikkaData.auth.isKeikkaAdmin}
            noDivider
          />
          <ProcessSubHeadingRow
            txt="Asiakas on syöttämässä tilaustietoja"
            show={keikkaData.auth.isKeikkaAdmin}
          />
          <ProcessRow
            txt={
              "Tilauksen luonti on aloitettu " +
              pvmString(parseJSONDate(thisProcess.luontiAloitettu.confirmDate), true)
            }
            done={thisProcess.luontiAloitettu.isConfirmed}
            isLoading={isLoading}
          />
          <ProcessRow
            txt="Kellonaika valittu"
            tip="Avaa kellonajan valintaikkuna"
            done={thisProcess.kellonaikaValittu.isConfirmed}
            onClick={() => navTo(3)}
            aria-label="Avaa kellonajan valintaikkuna"
            isLoading={isLoading}
          />
          <ProcessRow
            txt="Asiakas valittu"
            tip="Avaa asiakastietojen valintaikkuna"
            done={thisProcess.asiakasValittu.isConfirmed}
            onClick={() => navTo(1)}
            aria-label="Avaa asiakastietojen valintaikkuna"
            isLoading={isLoading}
          />
          <ProcessRow
            txt="Toimitusosoite valittu"
            tip="Avaa toimitusosoitteen valintaikkuna"
            done={thisProcess.toimitusOsoiteValittu.isConfirmed}
            onClick={() => navTo(1)}
            aria-label="Avaa toimitusosoitteen valintaikkuna"
            isLoading={isLoading}
          />
          <ProcessRow
            txt="Betonivalmistaja valittu"
            tip="Avaa betonitoimijoiden valintaikkuna"
            done={thisProcess.betoniToimijatValittu.isConfirmed}
            onClick={() => navTo(8)}
            aria-label="Avaa betonitoimijoiden valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasBetoni}
          />
          <ProcessRow
            txt="Pumpputoimittaja valittu"
            tip="Avaa betonitoimijoiden valintaikkuna"
            done={thisProcess.betoniToimijatValittu.isConfirmed}
            onClick={() => navTo(8)}
            aria-label="Avaa betonitoimijoiden valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasPumppu}
          />
          <ProcessRow
            txt="Puomi valittu"
            tip="Avaa puomin ja linjan valintaikkuna"
            done={thisProcess.puomiValittu.isConfirmed}
            onClick={() => navTo(2)}
            aria-label="Avaa puomin ja linjan valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasPumppu}
          />
          <ProcessRow
            txt="Linja valittu"
            tip="Avaa puomin ja linjan valintaikkuna"
            done={thisProcess.linjaValittu.isConfirmed}
            onClick={() => navTo(2)}
            aria-label="Avaa puomin ja linjan valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasPumppu}
          />
          <ProcessRow
            txt="Betonimäärä valittu"
            tip="Avaa betonin valintaikkuna"
            done={thisProcess.betoniMääräTäytetty.isConfirmed}
            onClick={() => {
              setKeikkaBetoniId(keikkaData.betoni[0].keikkaBetoniId);
              navTo(0);
            }}
            aria-label="Avaa betonin valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasBetoni}
          />
          <ProcessRow
            txt="Betonilaatu valittu"
            tip="Avaa betonin valintaikkuna"
            done={thisProcess.betoniTiedotTäytetty.isConfirmed}
            onClick={() => {
              setKeikkaBetoniId(keikkaData.betoni[0].keikkaBetoniId);
              navTo(4);
            }}
            aria-label="Avaa betonin valintaikkuna"
            isLoading={isLoading}
            show={keikkaData.modules.hasBetoni}
          />
          <ProcessRow
            txt="Tilaus lähetetty"
            tip={
              keikkaData.auth.isKeikkaAdmin
                ? "Avaa tilauksen vastaanotto- ja käsittelyikkuna"
                : "Lähetä tilaus"
            }
            done={thisProcess.tilausLähetetty.isConfirmed}
            pvm={thisProcess.tilausLähetetty.confirmDate}
            onClick={() => {
              if (keikkaData.tila.keikkaTilaId < 9) {
                if (keikkaData.auth.isKeikkaAdmin) setHandleKeikkaModalIsOpen(true);
                else setConfirmRightButtonModalIsOpen(true);
              }
            }}
            aria-label="Avaa tilauksen vastaanotto- ja käsittelyikkuna"
            isLoading={isLoading}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  function Arvosana() {
    return (
      keikkaData.tila.keikkaTilaId === 9 && ( // jos tilaus on toimitettu, avataan arvosana välilehti
        <>
          <Accordion expanded={expanded === "arvosana"} onChange={handleChange("arvosana")}>
            <AccordionSummary
              expandIcon={<ExpandCollapseIcon isLoading={starsLoading} />}
              aria-controls="arvosanabh-content"
              id="arvosanabh-header">
              <ProcessHeadingCell txt="Arvosana" width="45%" />
              <LazyLoader>
                <Stars
                  stars={{ stars: stars, palaute: undefined }}
                  isLoading={starsLoading}
                  precision={0.1}
                  readOnly
                  showNumber
                />
              </LazyLoader>
            </AccordionSummary>
            <AccordionDetails>
              <TabSubOtsikko
                txt="Tämä aukeaa ja käyttäjä voi antaa palautetta vain mikäli käyttäjä olisi saanut muokata tilausta luontivaiheessa"
                show={false}
                noDivider
              />
              <LazyLoader>
                <StarsSelectors
                  keikkaId={keikkaData.keikkaId}
                  onChange={handleStarsChange}
                  deliveryDate={keikkaData.pumppu.pumppuAika}
                />
              </LazyLoader>
            </AccordionDetails>
          </Accordion>
        </>
      )
    );
  }
}
StepProcess.propTypes = {
  keikkaData: PropTypes.object,
  navTo: PropTypes.func,
  setKeikkaData: PropTypes.func,
  reloadKeikkaData: PropTypes.func,
};
